/**
 * Staff chat (SR-14) wire types and their safe readers.
 *
 * Contract: staff-chat-contract (Scrips.Notifications, `/api/Notifications/staff-chat`). Bodies are camelCase JSON.
 * The readers keep every known field, drop unknown ones, and give a safe default for a missing or mistyped value,
 * so a newer server never breaks the screen ("Clients ignore unknown fields").
 *
 * PRIVACY: a message body and a patient name are PHI-adjacent. Nothing in the staff chat logs them, prints them to
 * the console or sends them to telemetry. Error messages carry the HTTP status and the error code only.
 */

export type StaffChatPersonKind = 'practitioner' | 'staff';

export interface StaffChatPerson {
  userId: string;
  name: string;
  kind: StaffChatPersonKind;
}

/** Resolved at read time, never stored. `name` is null when the patient can't be read. */
export interface StaffChatPatientRef {
  id: string;
  name: string | null;
  mrn: string | null;
}

export interface StaffChatMessage {
  id: string;
  threadId: string;
  senderUserId: string;
  /** '' when deleted. */
  body: string;
  /** ISO 8601 UTC. */
  createdAt: string;
  clientMessageId: string | null;
  isDeleted: boolean;
  /**
   * Client only, never on the wire: an optimistic message still on its way (`sending`) or refused (`failed`).
   * Absent on every message the server has confirmed.
   */
  localStatus?: 'sending' | 'failed';
}

export interface StaffChatLastMessage {
  id: string;
  senderUserId: string;
  /** At most 140 characters. */
  preview: string;
  createdAt: string;
}

export type StaffChatThreadKind = 'direct' | 'patient';

export interface StaffChatThread {
  id: string;
  kind: StaffChatThreadKind;
  /** Kind `patient` only. */
  patient: StaffChatPatientRef | null;
  /** Includes the caller. */
  participants: StaffChatPerson[];
  lastMessage: StaffChatLastMessage | null;
  /** For the caller. */
  unreadCount: number;
  updatedAt: string;
}

export interface StaffChatPage<T> {
  items: T[];
  nextCursor: string | null;
}

export type Raw = Record<string, unknown>;

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const obj = (v: unknown): Raw | null => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Raw) : null);
const objs = (v: unknown): Raw[] => (Array.isArray(v) ? v.map(obj).filter((x): x is Raw => x !== null) : []);

export function mapPerson(r: Raw): StaffChatPerson {
  return {
    userId: str(r.userId),
    name: str(r.name),
    kind: r.kind === 'practitioner' ? 'practitioner' : 'staff',
  };
}

export function mapPatientRef(r: Raw | null): StaffChatPatientRef | null {
  if (!r) return null;
  return { id: str(r.id), name: strOrNull(r.name), mrn: strOrNull(r.mrn) };
}

export function mapMessage(r: Raw): StaffChatMessage {
  return {
    id: str(r.id),
    threadId: str(r.threadId),
    senderUserId: str(r.senderUserId),
    body: str(r.body),
    createdAt: str(r.createdAt),
    clientMessageId: strOrNull(r.clientMessageId),
    isDeleted: r.isDeleted === true,
  };
}

export function mapThread(r: Raw): StaffChatThread {
  const last = obj(r.lastMessage);
  const unread = typeof r.unreadCount === 'number' && Number.isFinite(r.unreadCount) ? Math.max(0, r.unreadCount) : 0;
  return {
    id: str(r.id),
    kind: r.kind === 'patient' ? 'patient' : 'direct',
    patient: mapPatientRef(obj(r.patient)),
    participants: objs(r.participants).map(mapPerson),
    lastMessage: last
      ? {
          id: str(last.id),
          senderUserId: str(last.senderUserId),
          preview: str(last.preview),
          createdAt: str(last.createdAt),
        }
      : null,
    unreadCount: unread,
    updatedAt: str(r.updatedAt),
  };
}

export function mapPage<T>(raw: unknown, map: (r: Raw) => T): StaffChatPage<T> {
  const r = obj(raw) ?? {};
  return {
    items: objs(r.items).map(map),
    nextCursor: typeof r.nextCursor === 'string' && r.nextCursor ? r.nextCursor : null,
  };
}

/** The label a patient thread shows for its patient. */
export function patientLabel(patient: StaffChatPatientRef | null): string {
  return patient?.name ?? 'Patient record unavailable';
}
